import { BillSearchView } from '@enview/interface/types/BillSearch';
import { ReactElement, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useHistory } from 'react-router-dom';
import { SEOHead } from '../../components/head/SEOHead';
import { g as abilityGlossary } from '../../config/ability';
import { checkPermission, getBillSearchView, getTeamMode } from '../../dux';
import { State, Thunk } from '../../dux/@types';
import featureFlags, { FrontendFeature } from '../../featureFlags';
import STATES from '../../helpers/states';
import NavigationTab, { TabComponent } from '../Shared/NavigationTabs/NavigationTab';
import GatedFullScreenTakeover from '../Shared/Overlays/GatedFullScreenTakeover';
import Header from '../Shared/PageElements/Header';
import TeamBills from '../TeamView/TeamBills';
import JurisdictionHearings from './Hearings/JurisdictionHearings';
import JurisdictionBills from './JurisdictionBills';
import JurisdictionCommittees from './JurisdictionCommittees';
import JurisdictionLegislators from './JurisdictionLegislators';
import JurisdictionResources from './JurisdictionResources';
import JurisdictionSessions from './JurisdictionSessions';
import './Jurisdictions.scss';

type JurisdictionDetailsProps = {
  jurisAbbr: string;
};

enum Paths {
  MAIN = '/',
  BILLS = '/bills',
  LEGISLATORS = '/legislators',
  COMMITTEES = '/committees',
  RESOURCES = '/resources',
  HEARINGS = '/hearings',
}

const getPathPrefix = (jurisAbbr: string): string => `/jurisdictions/${jurisAbbr}`;

const getTabPath = (
  tabs: { [key: string]: string | TabComponent },
  tab: string | TabComponent,
  jurisAbbr: string,
): string => {
  const path = Object.keys(tabs).find(
    (key) =>
      tabs[key] === tab ||
      (!(tabs[key] as TabComponent)?.disabled &&
        (tabs[key] as TabComponent)?.name === tab),
  );
  return path ?? getPathPrefix(jurisAbbr);
};

const COMMITTEES_MODAL_HEADER = 'Upgrade your account to view committees';
const RESOURCES_MODAL_HEADER = 'Upgrade your account to view jurisdiction resources';

const JurisdictionDetails = (props: JurisdictionDetailsProps): ReactElement => {
  const orgUser = useSelector((state: State) => state.account.organizationUser);
  const dispatch = useDispatch();

  // Need to dispatch this action here instead of at root layout
  // due to nefarious Redux bug
  const teamModeId = useSelector(getTeamMode);
  useEffect(() => {
    dispatch(getBillSearchView(teamModeId));
  }, [dispatch, teamModeId]);
  const billSearchView =
    useSelector((state: State) => state.account.billSearchView) ?? BillSearchView.Card;

  const canViewTeams = (): Thunk<boolean> => {
    return dispatch(checkPermission(abilityGlossary.VIEW, abilityGlossary.TEAM, true));
  };

  const canViewCommitteesResources = (): Thunk<boolean> => {
    return dispatch(
      checkPermission(
        abilityGlossary.VIEW,
        abilityGlossary.JURISDICTION_RESOURCES,
        true,
      ),
    );
  };

  const canViewHearings = (): Thunk<boolean> => {
    return dispatch(checkPermission(abilityGlossary.VIEW, abilityGlossary.HEARINGS));
  };

  const isHearingsTabAvailableForUserFlag = featureFlags.isFeatureEnabledForUser(
    FrontendFeature.JurisdictionHearings,
    orgUser,
  );

  const getTabs = (jurisAbbr: string): { [key: string]: string | TabComponent } => {
    const prefix = getPathPrefix(jurisAbbr);
    const tabs: { [key: string]: string | TabComponent } = {};
    tabs[prefix] = 'Sessions';

    tabs[prefix + Paths.BILLS] = canViewTeams() ? 'Tracked Bills' : 'Bills';

    tabs[prefix + Paths.LEGISLATORS] = 'Legislators';
    tabs[prefix + Paths.COMMITTEES] = {
      name: 'Committees',
      disabled: !canViewCommitteesResources(),
    };

    tabs[prefix + Paths.RESOURCES] = {
      name: 'Resources',
      disabled: !canViewCommitteesResources(),
    };

    if (isHearingsTabAvailableForUserFlag)
      tabs[prefix + Paths.HEARINGS] = {
        name: 'Hearings',
        disabled: !canViewHearings(),
      };
    return tabs;
  };

  const { jurisAbbr } = props;
  const history = useHistory();
  const prefix = getPathPrefix(jurisAbbr);
  const tabs = useMemo(() => getTabs(jurisAbbr), [jurisAbbr]);
  const stateName = STATES[jurisAbbr.toUpperCase()];

  const renderJurisdictionBills = (): ReactElement => {
    return orgUser && canViewTeams() ? (
      <>
        <SEOHead
          description={`Bills tracked in ${stateName}`}
          title={`${stateName} Tracked Bills`}
        />
        <TeamBills
          filterToJurisdictions={[jurisAbbr]}
          noBillsMessage="Your team has not tracked any bills yet in this jurisdiction."
          orgUser={orgUser}
          showJurisdictionFilter={false}
          view={billSearchView}
        />
      </>
    ) : (
      <>
        <SEOHead description={`Bills in ${stateName}`} title={`${stateName} Bills`} />
        <JurisdictionBills jurisAbbr={jurisAbbr} />
      </>
    );
  };

  return (
    <div className="jurisdictions-view">
      <SEOHead description={`details on ${stateName}`} title={`${stateName}`} />
      {/* TODO: Make these route breadcrumbs? */}
      <Header title={`Explorer / ${stateName}`} />
      <NavigationTab
        currentTab={
          typeof tabs[window.location.pathname] === 'string'
            ? tabs[window.location.pathname].toString()
            : (tabs[window.location.pathname] as TabComponent)?.name
        }
        setCurrentTab={(tab) => history.push(getTabPath(tabs, tab, jurisAbbr))}
        tabs={Object.values(tabs)}
      >
        <div className="nav-tab">
          <Switch>
            <Route
              exact
              path={prefix}
              render={() => <JurisdictionSessions jurisAbbr={jurisAbbr} />}
            />
            <Route
              path={prefix + Paths.LEGISLATORS}
              render={() => <JurisdictionLegislators jurisAbbr={jurisAbbr} />}
            />
            <Route
              path={prefix + Paths.COMMITTEES}
              render={() => {
                return canViewCommitteesResources() ? (
                  <JurisdictionCommittees jurisAbbr={jurisAbbr} />
                ) : (
                  <GatedFullScreenTakeover modalHeader={COMMITTEES_MODAL_HEADER} />
                );
              }}
            />
            <Route
              path={prefix + Paths.RESOURCES}
              render={() => {
                return canViewCommitteesResources() ? (
                  <JurisdictionResources jurisAbbr={jurisAbbr} />
                ) : (
                  <GatedFullScreenTakeover modalHeader={RESOURCES_MODAL_HEADER} />
                );
              }}
            />
            <Route path={prefix + Paths.BILLS} render={renderJurisdictionBills} />
            {isHearingsTabAvailableForUserFlag && (
              <Route
                path={prefix + Paths.HEARINGS}
                render={() => {
                  return canViewHearings() ? (
                    <JurisdictionHearings jurisAbbr={jurisAbbr} />
                  ) : (
                    <GatedFullScreenTakeover />
                  );
                }}
              />
            )}
          </Switch>
        </div>
      </NavigationTab>
    </div>
  );
};

export default JurisdictionDetails;
